import React, { useEffect, useState } from 'react';
import {
  PageHeader, FormSection, SectionHeader, Button, TextButton,
  Field, Input, Toggle, CheckRow, TYPE, cn,
} from './ui';
import { settingsApi, SmtpSettings, ReminderSettings, expensesApi, userApi } from '../services/api';

export const Settings: React.FC = () => {
  const [smtpLoaded, setSmtpLoaded] = useState<SmtpSettings | null>(null);
  const [smtpForm, setSmtpForm] = useState({
    host: '',
    port: 587,
    secure: false, // port 587 uses STARTTLS (secure=false). Toggle true for port 465.
    username: '',
    password: '',
    fromAddress: '',
    fromName: '',
  });
  const [reminders, setReminders] = useState<ReminderSettings>({
    enabled: false,
    daysBefore: 3,
    sendHour: 8,
    remindBills: true,
    billCategories: null,
    remindCreditCards: true,
    remindLoans: true,
  });
  const [availableCategories, setAvailableCategories] = useState<string[]>([]);
  const [threshold, setThreshold] = useState<number>(0);
  const [thresholdStatus, setThresholdStatus] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });
  const [savingThreshold, setSavingThreshold] = useState(false);
  const [smtpStatus, setSmtpStatus] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });
  const [reminderStatus, setReminderStatus] = useState<{ type: 'success' | 'error' | ''; text: string }>({ type: '', text: '' });
  const [testing, setTesting] = useState(false);
  const [savingSmtp, setSavingSmtp] = useState(false);
  const [savingReminders, setSavingReminders] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [smtp, rem, expenses, profile] = await Promise.all([
          settingsApi.getSmtp().catch(() => null),
          settingsApi.getReminders().catch(() => null),
          expensesApi.getAll().catch(() => []),
          userApi.getProfile().catch(() => null),
        ]);
        if (profile) setThreshold(profile.lowBalanceThreshold ?? 0);
        if (smtp) {
          setSmtpLoaded(smtp);
          setSmtpForm({
            host: smtp.host || '',
            port: smtp.port || 587,
            secure: smtp.secure !== false,
            username: smtp.username || '',
            password: '',
            fromAddress: smtp.fromAddress || '',
            fromName: smtp.fromName || '',
          });
        }
        if (rem) setReminders({ ...rem, billCategories: rem.billCategories ?? null });
        const cats = Array.from(new Set(expenses.map((e: any) => e.category).filter(Boolean))).sort() as string[];
        setAvailableCategories(cats);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const handleSaveThreshold = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingThreshold(true);
    setThresholdStatus({ type: '', text: '' });
    try {
      await userApi.updateThreshold(threshold);
      setThresholdStatus({
        type: 'success',
        text: threshold > 0
          ? `Saved. You'll be warned when your projected balance drops below $${Math.round(threshold).toLocaleString('en-US')}.`
          : 'Saved. Low-balance warnings are off.',
      });
    } catch (err: any) {
      setThresholdStatus({ type: 'error', text: err?.message || 'Failed to save.' });
    } finally {
      setSavingThreshold(false);
    }
  };

  const handleSaveSmtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingSmtp(true);
    setSmtpStatus({ type: '', text: '' });
    try {
      const payload: any = {
        host: smtpForm.host,
        port: Number(smtpForm.port),
        secure: smtpForm.secure,
        username: smtpForm.username,
        fromAddress: smtpForm.fromAddress,
        fromName: smtpForm.fromName || undefined,
      };
      if (smtpForm.password) payload.password = smtpForm.password;
      const saved = await settingsApi.saveSmtp(payload);
      setSmtpLoaded(saved);
      setSmtpForm(f => ({ ...f, password: '' }));
      setSmtpStatus({ type: 'success', text: 'SMTP settings saved.' });
    } catch (err: any) {
      setSmtpStatus({ type: 'error', text: err?.message || 'Failed to save SMTP settings.' });
    } finally {
      setSavingSmtp(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setSmtpStatus({ type: '', text: '' });
    try {
      await settingsApi.testSmtp();
      setSmtpStatus({ type: 'success', text: 'Test email sent. Check your inbox.' });
    } catch (err: any) {
      setSmtpStatus({ type: 'error', text: err?.message || 'Test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const handleDeleteSmtp = async () => {
    if (!window.confirm('Remove SMTP settings? Reminders will stop sending until you configure them again.')) return;
    try {
      await settingsApi.deleteSmtp();
      setSmtpLoaded(null);
      setSmtpForm({ host: '', port: 587, secure: true, username: '', password: '', fromAddress: '', fromName: '' });
      setSmtpStatus({ type: 'success', text: 'SMTP settings removed.' });
    } catch (err: any) {
      setSmtpStatus({ type: 'error', text: err?.message || 'Failed to remove SMTP settings.' });
    }
  };

  const handleSaveReminders = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingReminders(true);
    setReminderStatus({ type: '', text: '' });
    try {
      const saved = await settingsApi.saveReminders(reminders);
      setReminders(saved);
      setReminderStatus({ type: 'success', text: 'Reminder settings saved.' });
    } catch (err: any) {
      setReminderStatus({ type: 'error', text: err?.message || 'Failed to save.' });
    } finally {
      setSavingReminders(false);
    }
  };

  if (isLoading) {
    return (
      <>
        <PageHeader heading="Settings" />
        <p className={cn(TYPE.meta, 'text-ink-muted mt-11')}>Loading settings…</p>
      </>
    );
  }

  /** Status line. Success is quiet ink; failure is the one thing on the row
   *  that earns red. */
  const status = (s: { type?: string; text?: string }) =>
    s.text ? (
      <p className={cn(TYPE.metaTight, s.type === 'success' ? 'text-ink-muted' : 'text-bad')}>{s.text}</p>
    ) : null;

  return (
    <>
      <PageHeader
        heading="Settings"
        subhead="Alerts, the mail server that sends them, and what you get reminded about."
      />

      <form onSubmit={handleSaveThreshold} className="mt-11">
        <FormSection
          title="Low balance alerts"
          index="01"
          hint="Warns when your projected checking balance is about to get low — on the Dashboard, on Calendar days, and while building a payoff plan."
        >
          <div className="max-w-[280px]">
            <Field
              label="Warn below"
              hint="Set to 0 to turn warnings off. A negative balance is always flagged."
              htmlFor="threshold"
            >
              <Input
                id="threshold"
                type="number"
                min={0}
                step="1"
                value={threshold}
                onChange={e => setThreshold(Math.max(0, parseFloat(e.target.value) || 0))}
              />
            </Field>
          </div>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            {status(thresholdStatus)}
            <Button type="submit" variant="primary" disabled={savingThreshold} className="ml-auto">
              {savingThreshold ? 'Saving…' : 'Save alerts'}
            </Button>
          </div>
        </FormSection>
      </form>

      <form onSubmit={handleSaveSmtp}>
        <FormSection
          title="Email (SMTP)"
          index="02"
          hint="Your own mail server sends the reminders. Gmail is smtp.gmail.com:465 with SSL; SendGrid, Mailgun and your own domain all work too."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6">
            <Field label="SMTP host" htmlFor="smtp-host">
              <Input
                id="smtp-host"
                type="text"
                required
                value={smtpForm.host}
                onChange={e => setSmtpForm({ ...smtpForm, host: e.target.value })}
                placeholder="smtp.gmail.com"
              />
            </Field>
            <Field label="Port" hint="Common: 587 (STARTTLS) or 465 (SSL/TLS)." htmlFor="smtp-port">
              <Input
                id="smtp-port"
                type="number"
                required
                value={smtpForm.port}
                onChange={e => {
                  const port = parseInt(e.target.value) || 587;
                  // Auto-pair secure with the common ports so users don't hit
                  // "wrong version number" TLS handshake errors.
                  const secure = port === 465 ? true : port === 587 || port === 25 ? false : smtpForm.secure;
                  setSmtpForm({ ...smtpForm, port, secure });
                }}
              />
            </Field>
            <Field label="Username" htmlFor="smtp-user">
              <Input
                id="smtp-user"
                type="text"
                required
                value={smtpForm.username}
                onChange={e => setSmtpForm({ ...smtpForm, username: e.target.value })}
              />
            </Field>
            <Field
              label="Password"
              hint={smtpLoaded ? 'Leave blank to keep the stored password.' : undefined}
              htmlFor="smtp-pass"
            >
              <Input
                id="smtp-pass"
                type="password"
                value={smtpForm.password}
                onChange={e => setSmtpForm({ ...smtpForm, password: e.target.value })}
                placeholder={smtpLoaded ? '••••••••' : ''}
              />
            </Field>
            <Field label="From address" htmlFor="smtp-from">
              <Input
                id="smtp-from"
                type="email"
                required
                value={smtpForm.fromAddress}
                onChange={e => setSmtpForm({ ...smtpForm, fromAddress: e.target.value })}
                placeholder="you@example.com"
              />
            </Field>
            <Field label="From name" hint="Optional." htmlFor="smtp-fromname">
              <Input
                id="smtp-fromname"
                type="text"
                value={smtpForm.fromName}
                onChange={e => setSmtpForm({ ...smtpForm, fromName: e.target.value })}
                placeholder="SmartySpend"
              />
            </Field>
          </div>

          <div className="mt-7 max-w-[520px]">
            <Toggle
              checked={smtpForm.secure}
              onChange={next => setSmtpForm({ ...smtpForm, secure: next })}
              label="Use SSL/TLS from the start"
              hint="Port 465. Turn off for port 587, which upgrades with STARTTLS."
            />
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-4">
            {status(smtpStatus)}
            <div className="ml-auto flex flex-wrap items-center gap-4">
              {smtpLoaded && (
                <Button type="button" variant="danger" onClick={handleDeleteSmtp}>Remove</Button>
              )}
              <TextButton type="button" disabled={!smtpLoaded || testing} onClick={handleTest}>
                {testing ? 'Sending…' : 'Send test email'}
              </TextButton>
              <Button type="submit" variant="primary" disabled={savingSmtp}>
                {savingSmtp ? 'Saving…' : 'Save SMTP'}
              </Button>
            </div>
          </div>
        </FormSection>
      </form>

      <form onSubmit={handleSaveReminders}>
        <FormSection
          title="Payment reminders"
          index="03"
          hint="An email a few days before scheduled payments are due. Needs the SMTP settings above."
        >
          <div className="max-w-[520px]">
            <Toggle
              checked={reminders.enabled}
              onChange={next => setReminders({ ...reminders, enabled: next })}
              label="Send payment reminders"
            />
          </div>

          {reminders.enabled && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-6 mt-7 max-w-[560px]">
                <Field label="Days before due" htmlFor="rem-days">
                  <Input
                    id="rem-days"
                    type="number"
                    min={0}
                    max={30}
                    value={reminders.daysBefore}
                    onChange={e => setReminders({ ...reminders, daysBefore: parseInt(e.target.value) || 0 })}
                  />
                </Field>
                <Field label="Send hour" hint="Server time, 0–23." htmlFor="rem-hour">
                  <Input
                    id="rem-hour"
                    type="number"
                    min={0}
                    max={23}
                    value={reminders.sendHour}
                    onChange={e => setReminders({ ...reminders, sendHour: parseInt(e.target.value) || 0 })}
                  />
                </Field>
              </div>

              <div className="mt-9 max-w-[560px]">
                <SectionHeader label="Remind me about" />
                <div className="mt-4">
                  <Toggle
                    checked={reminders.remindBills}
                    onChange={next => setReminders({ ...reminders, remindBills: next })}
                    label="Bills and expenses"
                  />
                </div>

                {reminders.remindBills && (
                  <div className="mt-4 ml-[46px]">
                    <div className="flex flex-wrap items-center gap-4 mb-2">
                      <span className={cn(TYPE.metaTight, 'text-ink-muted')}>Categories</span>
                      {availableCategories.length > 0 && (
                        <div className="flex items-center gap-4">
                          <TextButton
                            type="button"
                            onClick={() => setReminders({ ...reminders, billCategories: null })}
                          >
                            All
                          </TextButton>
                          <TextButton
                            type="button"
                            onClick={() => setReminders({ ...reminders, billCategories: [] })}
                          >
                            None
                          </TextButton>
                        </div>
                      )}
                    </div>

                    {availableCategories.length === 0 ? (
                      <p className={cn(TYPE.small, 'text-ink-muted')}>
                        No bill categories yet. Add expenses first, or leave this on to cover future categories.
                      </p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                        {availableCategories.map(cat => {
                          // null means "all" — treat as everything checked
                          const selected = reminders.billCategories === null
                            ? true
                            : reminders.billCategories.includes(cat);
                          return (
                            <CheckRow
                              key={cat}
                              checked={selected}
                              label={cat}
                              onChange={next => {
                                const current = reminders.billCategories === null
                                  ? [...availableCategories]
                                  : [...reminders.billCategories];
                                const updated = next
                                  ? Array.from(new Set([...current, cat]))
                                  : current.filter(c => c !== cat);
                                // Every option on reverts to null (= all).
                                const allSelected = availableCategories.every(c => updated.includes(c));
                                setReminders({
                                  ...reminders,
                                  billCategories: allSelected ? null : updated,
                                });
                              }}
                            />
                          );
                        })}
                      </div>
                    )}

                    {reminders.billCategories && reminders.billCategories.length === 0 && (
                      // Nothing is broken here — you simply will not be
                      // reminded. That is warn, not bad, and the glyph carries
                      // it as much as the ochre does.
                      <p className={cn(TYPE.metaTight, 'text-warn mt-3')}>
                        ▲ No categories selected — no bill reminders will be sent
                      </p>
                    )}
                  </div>
                )}

                <div className="mt-2">
                  <Toggle
                    checked={reminders.remindCreditCards}
                    onChange={next => setReminders({ ...reminders, remindCreditCards: next })}
                    label="Credit card payments"
                  />
                  <Toggle
                    checked={reminders.remindLoans}
                    onChange={next => setReminders({ ...reminders, remindLoans: next })}
                    label="Loan payments"
                  />
                </div>
              </div>
            </>
          )}

          <div className="mt-7 flex flex-wrap items-center justify-between gap-4">
            {status(reminderStatus)}
            <Button type="submit" variant="primary" disabled={savingReminders} className="ml-auto">
              {savingReminders ? 'Saving…' : 'Save reminders'}
            </Button>
          </div>
        </FormSection>
      </form>
    </>
  );
};
